/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { HTMLAttributes, useEffect, useState } from 'preact/compat';
import { Container } from '@adobe-commerce/elsie/lib';
import { QuoteCommentsList as QuoteCommentsListComponent } from '@/quote-management/components/QuoteCommentsList';
import { NegotiableQuoteModel } from '@/quote-management/data/models/negotiable-quote-model';
import { events } from '@adobe-commerce/event-bus';
import { formattedDate } from '@/quote-management/utils/dateUtils';

export interface QuoteCommentsListProps
  extends HTMLAttributes<HTMLUListElement> {
  quoteData?: NegotiableQuoteModel;
}

export const QuoteCommentsList: Container<QuoteCommentsListProps> = ({
  quoteData: initialData,
  ...props
}) => {
  const [quoteData, setQuoteData] = useState<NegotiableQuoteModel | undefined>(
    initialData
  );

  useEffect(() => {
    const quoteDataEvent = events.on(
      'quote-management/quote-data',
      (data: { quote: NegotiableQuoteModel }) => {
        setQuoteData(data.quote);
      },
      {
        eager: true,
      }
    );
    return () => quoteDataEvent?.off();
  }, []);

  if (!quoteData) {
    return null;
  }

  // Format comments data
  const formattedComments = quoteData?.comments?.map((comment) => ({
    uid: comment.uid,
    createdAt: <span>{formattedDate(comment.createdAt, 'long')}</span>,
    author: (
      <span>
        {comment.author.firstname} {comment.author.lastname}
      </span>
    ),
    text: <p>{comment.text}</p>,
    attachments: comment.attachments,
  }));

  return (
    <QuoteCommentsListComponent
      data-testid="quote-comments-list-container"
      {...props}
      comments={formattedComments || []}
    />
  );
};
